import * as React from 'react';
import { Stack, Typography } from '@mui/material';

import {
    buildMonitoringIlStatusNestedGroups,
    formatMonitoringIlStatusDisplayLine,
    formatMonitoringIlStatusGroupLabel,
    isMonitoringIlStatusAliasKeyEmpty,
    shipmentMonitoringCellTypographySx,
} from './shipmentMonitoringDefShared';

/** alias_id 헤더용 연두색 */
const MONITORING_ALIAS_HEADER_COLOR = '#9ccc65';

const lineTypographySx = {
    ...shipmentMonitoringCellTypographySx,
    display: 'block',
    lineHeight: 1.25,
};

function warningMessageLine(entry) {
    if (!entry || typeof entry !== 'object') return '';
    const msg = entry.message != null ? String(entry.message).trim() : '';
    if (msg) return msg;
    return formatMonitoringIlStatusDisplayLine(entry);
}

/** @param {'hbl' | 'hawb'} prefixTag */
function formatHouseHeaderWithWaybillPrefix(houseId, prefixTag) {
    const v = formatMonitoringIlStatusGroupLabel(houseId).trim();
    const prefix = prefixTag === 'hawb' ? 'HAWB#' : 'HBL#';
    if (!v) return prefix;
    return `${prefix} ${v}`;
}

/** @param {'hbl' | 'hawb' | 'ftl'} houseHeaderStyle — Air: HAWB#, Ocean: HBL#, FTL/LTL: 접두 없음 */
function formatHouseGroupTitle(houseId, houseHeaderStyle) {
    if (houseHeaderStyle === 'ftl') {
        return formatMonitoringIlStatusGroupLabel(houseId).trim();
    }
    if (houseHeaderStyle === 'hawb') {
        return formatHouseHeaderWithWaybillPrefix(houseId, 'hawb');
    }
    return formatHouseHeaderWithWaybillPrefix(houseId, 'hbl');
}

export function renderMonitoringIlStatusCell(p, options = {}) {
    const houseHeaderStyle = options.houseHeaderStyle ?? 'hbl';
    const raw = p.row?.il_status ?? p.row?.status;
    const houseGroups = buildMonitoringIlStatusNestedGroups(raw);
    if (!houseGroups.length) {
        return <Typography sx={lineTypographySx} />;
    }
    return (
        <Stack
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                width: '100%',
                minHeight: 0,
                gap: 0.15,
                py: 0.25,
            }}
        >
            {houseGroups.map((houseGroup, hi) => (
                <Stack
                    key={`house-${hi}-${houseGroup.houseId ?? 'x'}`}
                    spacing={0.2}
                    sx={{ mt: hi > 0 ? 0.75 : 0 }}
                >
                    <Typography
                        component="div"
                        sx={{
                            ...lineTypographySx,
                            fontWeight: 700,
                        }}
                    >
                        {formatHouseGroupTitle(houseGroup.houseLabel, houseHeaderStyle)}
                    </Typography>
                    {houseGroup.warnings.map((w, wi) => (
                        <Typography
                            key={w.id ?? `warn-${hi}-${wi}`}
                            component="div"
                            sx={{
                                ...lineTypographySx,
                                fontWeight: 400,
                                color: 'error.main',
                            }}
                        >
                            {warningMessageLine(w)}
                        </Typography>
                    ))}
                    {houseGroup.children.map((childGroup, ci) => (
                        <Stack
                            key={`child-${ci}-${childGroup.childId ?? 'x'}`}
                            spacing={0.1}
                            sx={{ width: '100%' }}
                        >
                            {!isMonitoringIlStatusAliasKeyEmpty(childGroup.childId) ? (
                                <Typography component="div" sx={{ ...lineTypographySx, fontWeight: 500 }}>
                                    {formatMonitoringIlStatusGroupLabel(childGroup.childId)}
                                </Typography>
                            ) : null}
                            {childGroup.aliases.map((aliasGroup, ai) => (
                                <Stack
                                    key={`alias-${ci}-${ai}-${String(aliasGroup.aliasId)}`}
                                    spacing={0.08}
                                    sx={{ width: '100%' }}
                                >
                                    {!isMonitoringIlStatusAliasKeyEmpty(aliasGroup.aliasId) ? (
                                        <Typography
                                            component="div"
                                            sx={{
                                                ...lineTypographySx,
                                                fontWeight: 500,
                                                color: MONITORING_ALIAS_HEADER_COLOR,
                                            }}
                                        >
                                            {formatMonitoringIlStatusGroupLabel(aliasGroup.aliasId)}
                                        </Typography>
                                    ) : null}
                                    {aliasGroup.entries.map((entry, ei) => (
                                        <Typography
                                            key={entry.id ?? `entry-${hi}-${ci}-${ai}-${ei}`}
                                            component="div"
                                            sx={{ ...lineTypographySx, fontWeight: 400 }}
                                        >
                                            {formatMonitoringIlStatusDisplayLine(entry)}
                                        </Typography>
                                    ))}
                                </Stack>
                            ))}
                        </Stack>
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}

/**
 * DataGrid Status (`row.il_status` / `row.status`).
 * - Air: `getShipmentMonitoringIlStatusColumn({ statusHouseHeaderStyle: 'hawb' })`
 * - FTL/LTL: `getShipmentMonitoringIlStatusColumn({ statusHouseHeaderStyle: 'ftl' })`
 */
export function getShipmentMonitoringIlStatusColumn(overrides = {}) {
    const { statusHouseHeaderStyle = 'hbl', ...columnRest } = overrides;
    return {
        field: 'status',
        headerName: 'Status',
        width: 300,
        minWidth: 80,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        ...columnRest,
        renderCell: (p) => renderMonitoringIlStatusCell(p, { houseHeaderStyle: statusHouseHeaderStyle }),
    };
}
